import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  CalendarCheck, 
  Calendar, 
  Users, 
  Briefcase, 
  Building2, 
  Clock, 
  UserCheck, 
  Star, 
  FileText, 
  Phone, 
  Share2, 
  Bell, 
  Languages, 
  Image, 
  Settings, 
  ShieldCheck, 
  Activity, 
  Scale,
  CreditCard,
  MessageSquare,
  LogOut,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../common/Toast';

export default function AdminSidebar({ pendingCount = 0, isOpen = false, onClose = () => {}, isAccessLocked = false }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isLawyer = user?.role === 'LAWYER';
  const isClient = user?.role === 'CLIENT' || user?.isClient;

  const handleLogout = () => {
    showToast('Tizimdan muvaffaqiyatli chiqildi', 'info');
    logout();
    navigate('/admin/login', { replace: true });
    window.location.href = '/admin/login';
  };

  const lawyerMenuSections = [
    {
      title: 'Advokat Maydoni',
      items: [
        { to: '/admin/dashboard', label: 'Boshqaruv Paneli', icon: LayoutDashboard },
        { 
          to: '/admin/appointments', 
          label: 'Mening Qabullarim', 
          icon: CalendarCheck, 
          badge: pendingCount > 0 ? pendingCount : null 
        },
        { to: '/admin/calendar', label: 'Mening Taqvimim', icon: Calendar },
        { to: '/admin/lawyer-chat', label: 'Mijozlar Bilan Chat', icon: MessageSquare },
        { to: '/admin/clients', label: 'Mening Mijozlarim', icon: UserCheck },
      ],
    },
    {
      title: 'Sud va Amaliyot',
      items: [
        { to: '/admin/lawyers', label: 'Sud Kundaligi & Dosye', icon: Users },
        { to: '/admin/working-hours', label: 'Ish Jadvalim & Ta’tillar', icon: Clock },
      ],
    },
    {
      title: 'Shaxsiy Tizim',
      items: [
        { to: '/admin/subscriptions', label: 'Mening Obunam (99k)', icon: CreditCard },
        { to: '/admin/notifications', label: 'Bildirishnomalar', icon: Bell },
        { to: '/admin/settings', label: 'Profil Sozlamalari', icon: Settings },
      ],
    },
  ];

  const clientMenuSections = [
    {
      title: 'Mijoz Xizmatlari',
      items: [
        { to: '/admin/dashboard', label: 'Boshqaruv Paneli', icon: LayoutDashboard },
        { 
          to: '/admin/appointments', 
          label: 'Mening Arizalarim', 
          icon: CalendarCheck, 
          badge: pendingCount > 0 ? pendingCount : null 
        },
        { to: '/admin/lawyer-chat', label: 'Advokat Bilan Chat', icon: MessageSquare },
      ],
    },
    {
      title: 'Hisob & To‘lov',
      items: [
        { to: '/admin/subscriptions', label: 'Mening Obunam (35k)', icon: CreditCard },
        { to: '/admin/notifications', label: 'Bildirishnomalar', icon: Bell },
        { to: '/admin/settings', label: 'Profil Sozlamalari', icon: Settings },
      ],
    },
  ];

  const menuSections = [
    {
      title: 'Boshqaruv',
      items: [
        { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { 
          to: '/admin/appointments', 
          label: 'Uchrashuvlar', 
          icon: CalendarCheck, 
          badge: pendingCount > 0 ? pendingCount : null 
        },
        { to: '/admin/calendar', label: 'Taqvim (Calendar)', icon: Calendar },
        { to: '/admin/clients', label: 'Mijozlar bazasi', icon: UserCheck },
      ],
    },
    {
      title: 'To‘lovlar & Nazorat',
      items: [
        { to: '/admin/subscriptions', label: 'To‘lovlar & Obunalar', icon: CreditCard },
        { to: '/admin/chats', label: 'Yozishmalar Nazorati', icon: MessageSquare },
      ],
    },
    {
      title: 'Yuridik Amaliyot',
      items: [
        { to: '/admin/lawyers', label: 'Advokatlar', icon: Users },
        { to: '/admin/services', label: 'Xizmatlar & Amaliyot', icon: Briefcase },
        { to: '/admin/offices', label: 'Ofislar & Joylashuv', icon: Building2 },
        { to: '/admin/working-hours', label: 'Ish Vaqtlari & Ta’tillar', icon: Clock },
      ],
    },
    {
      title: 'Kontent & CMS',
      items: [
        { to: '/admin/cms', label: 'Sayt Kontenti (CMS)', icon: FileText },
        { to: '/admin/reviews', label: 'Mijozlar fikrlari', icon: Star },
        { to: '/admin/contact', label: 'Kontakt ma’lumotlari', icon: Phone },
        { to: '/admin/social', label: 'Ijtimoiy tarmoqlar', icon: Share2 },
        { to: '/admin/translations', label: 'Tillar & Tarjimalar', icon: Languages },
        { to: '/admin/media', label: 'Media & Rasmlar', icon: Image },
      ],
    },
    {
      title: 'Tizim & Xavfsizlik',
      items: [
        { to: '/admin/notifications', label: 'Bildirishnomalar', icon: Bell },
        { to: '/admin/settings', label: 'Tizim Sozlamalari', icon: Settings },
        { to: '/admin/users', label: 'Admin Foydalanuvchilar', icon: ShieldCheck, superAdminOnly: true },
        { to: '/admin/activity-logs', label: 'Audit & Harakatlar tarixi', icon: Activity },
      ],
    },
  ];

  const sectionsToRender = isLawyer 
    ? lawyerMenuSections 
    : isClient 
    ? clientMenuSections 
    : menuSections;

  return (
    <>
      {/* Mobile Drawer Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/75 backdrop-blur-xs z-40 lg:hidden transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#091322] border-r border-[#192842] flex flex-col h-full select-none shrink-0 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Brand Header */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-[#192842] bg-[#050b14]/50">
          <div className="flex items-center min-w-0">
            <Scale className="w-6 h-6 text-[#c5a880] mr-2.5 shrink-0" />
            <div className="overflow-hidden">
              <span className="font-serif text-sm font-bold text-slate-100 tracking-wider block truncate">
                {isLawyer ? 'ADVOKAT PORTALI' : isClient ? 'MIJOZ PORTALI' : 'JIZZAX ADVOKATLARI'}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#c5a880] block truncate">
                {isLawyer ? (user?.fullName || 'Shaxsiy Kabinet') : isClient ? (user?.fullName || 'Mijoz Kabineti') : 'Management System'}
              </span>
            </div>
          </div>
          {/* Close button for mobile/tablet */}
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
            aria-label="Yopish"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

      {/* Nav Menu Sections */}
      <div className="flex-1 px-3 py-4 space-y-6">
        {sectionsToRender.map((section, idx) => (
          <div key={idx}>
            <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {section.title}
            </div>
            <ul className="space-y-1">
              {section.items.map((item) => {
                if (item.superAdminOnly && user?.role !== 'SUPER_ADMIN') {
                  return null;
                }
                const Icon = item.icon;
                const isLocked = isAccessLocked && item.to !== '/admin/subscriptions';
                return (
                  <li key={item.to}>
                    <NavLink
                      to={isLocked ? '/admin/subscriptions' : item.to}
                      onClick={(e) => {
                        if (isLocked) {
                          e.preventDefault();
                          showToast('Tizimdan foydalanish uchun obunani faollashtiring yoki ruxsat oling', 'error');
                          navigate('/admin/subscriptions');
                        }
                      }}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-3 py-2 rounded text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-[#1b2b46] text-[#c5a880] font-semibold border-l-2 border-[#c5a880]'
                            : isLocked
                            ? 'text-slate-500 hover:text-amber-400 hover:bg-[#11223b]/60'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-[#11223b]'
                        }`
                      }
                    >
                      <div className="flex items-center min-w-0">
                        <Icon className="w-4 h-4 mr-2.5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </div>
                      {isLocked ? (
                        <Lock className="w-3.5 h-3.5 text-amber-400 shrink-0 ml-1.5" title="Obuna talab qilinadi" />
                      ) : item.badge ? (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-slate-900 animate-pulse">
                          {item.badge}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* User Info & Logout Footer */}
      <div className="p-4 border-t border-[#192842] bg-[#050b14]/40">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-200 truncate">
              {user?.fullName || 'Admin User'}
            </div>
            <div className="text-[10px] text-[#c5a880] font-mono truncate">
              {user?.role || 'ADMIN'}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-rose-300 hover:text-rose-100 bg-rose-950/30 hover:bg-rose-900/50 border border-rose-900/50 rounded transition-colors cursor-pointer"
        >
          <LogOut className="w-3.5 h-3.5 mr-1.5" />
          Tizimdan chiqish
        </button>
      </div>
    </aside>
    </>
  );
}
